import { createSelector } from 'reselect';

import {
  isNameValid,
  notEmptyField,
  validateBCNumber,
  validateDocumentDivisionCode,
  validateDocumentIssueDate,
  validateDocumentNumber,
} from './common';
import {
  child,
  childAddressMatchesNoOne,
  shouldValidateChildAddress,
  childShouldHaveBirthCertificate,
  childShouldHavePassport,
  childShouldHaveRussianBirthCertificate,
  childShouldHaveRussianPassport,
  getActiveChildBirthCertificate,
  getActiveChildPassport,
  getActiveChildSecondParent,
  getChildAddress,
  hasDeathCertificate,
  isChild18YearsOld,
  isPaternity,
  isPaternityOrDivorced,
  isSecondParentDivorced,
  isWidowed,
  secondParentStatus,
  shouldBeGettingEducationSelector,
  showEducationSelector,
  showHomestayDocumentsSelector,
} from '../selectors/child';
import { isApplicantMarried } from '../selectors/applicant';
import { isSpouseRegisteredInMoscow } from '../selectors/spouse';
import {
  validateAddressAddress,
  validateAddressArea,
  validateAddressDistrict,
  validateAddressFlat,
  validateAddressHouseAndBuilding,
  validateAddressOMK,
  validateAddressStreet,
} from '../selectors/address';
import {
  ChildFields,
  DeadSecondParent,
  DivorcedSecondParent,
  PaternitySecondParent,
} from '../models/child';
import {
  validatePersonChangedName,
  validatePersonChangedNameDocIssuer,
  validatePersonChangedPatronimic,
  validatePersonChangedSurname,
  validatePersonChangeName,
} from '../selectors/person';
import { isDateInvalid, isDateInAcceptableRange } from '../helpers/dateHelpers';
import { BORN_IN } from '../constants/child';
import { ApplicantFields } from '../models/applicant';
import {
  validateBirthCertificateIssueDate,
  validateChildBirthdateWithPaternityDate,
  validateMarriageAndDivorceDates,
  validateMarriageDateAndApplicantBirthDate,
  validateSecondParentBirthdateAndDeathdate,
} from './dates';

// child personal info

export const validateChildName = createSelector(child, ({ name }) =>
  isNameValid(name.value)
);

export const validateChildSurname = createSelector(child, ({ surname }) =>
  isNameValid(surname.value)
);

export const validateChildPatronymic = createSelector(child, ({ patronymic }) =>
  isNameValid(patronymic.value)
);

export const validateChildSex = createSelector(child, ({ sex }) => !sex.value);

export const validateChildChangeNameCheckbox = createSelector(child, (person) =>
  validatePersonChangeName({ person })
);

export const validateChildChangedName = createSelector(child, (person) =>
  validatePersonChangedName({ person })
);

export const validateChildChangedSurname = createSelector(child, (person) =>
  validatePersonChangedSurname({ person })
);

export const validateChildChangedPatronimic = createSelector(child, (person) =>
  validatePersonChangedPatronimic({ person })
);

export const validateChildChangedNameIssuer = createSelector(child, (person) =>
  validatePersonChangedNameDocIssuer({ person })
);

// child documents

export const validateChildCitizenship = createSelector(
  child,
  ({ citizenship }) => !citizenship.value
);

export const validateChildBirthCertificateType = createSelector(
  childShouldHaveBirthCertificate,
  (haveBirthCertificate) => !haveBirthCertificate
);

export const validateChildBirthCertificateNumber = createSelector(
  childShouldHaveBirthCertificate,
  childShouldHaveRussianBirthCertificate,
  getActiveChildBirthCertificate,
  (haveBirthCertificate, isRussianBC, { number: { value } }) =>
    haveBirthCertificate && validateBCNumber(isRussianBC, value)
);

export const validateChildBirthCertificateIssueDate = createSelector(
  childShouldHaveBirthCertificate,
  getActiveChildBirthCertificate,
  validateBirthCertificateIssueDate,
  (haveBirthCertificate, { issueDate: { value } }, error) => {
    return haveBirthCertificate && (isDateInAcceptableRange(value) || error);
  }
);

export const validateChildBirthCertificateIssuer = createSelector(
  childShouldHaveBirthCertificate,
  getActiveChildBirthCertificate,
  (haveBirthCertificate, { issuer }) =>
    haveBirthCertificate && notEmptyField(issuer.value)
);

// child russian passport

export const validateChildDocumentType = createSelector(
  childShouldHavePassport,
  getActiveChildPassport,
  (hasDocument, { type }) => hasDocument && !type.value
);

export const validateChildDocumentNumber = createSelector(
  getActiveChildPassport,
  childShouldHavePassport,
  childShouldHaveRussianPassport,
  ({ number }, hasDocument, isRussianDocument) =>
    hasDocument && validateDocumentNumber(isRussianDocument, number.value)
);

export const validateChildDocumentIssuer = createSelector(
  getActiveChildPassport,
  childShouldHavePassport,
  ({ issuer }, hasDocument) => hasDocument && notEmptyField(issuer.value)
);

export const validateChildDocumentIssuedDate = createSelector(
  child,
  childShouldHavePassport,
  childShouldHaveRussianPassport,
  getActiveChildPassport,
  ({ birthdate }, hasDocument, isRussianPassport, { issueDate: { value } }) => {
    return (
      hasDocument &&
      validateDocumentIssueDate(value, birthdate.value, isRussianPassport)
    );
  }
);

export const validateChildBirthPlace = createSelector(
  child,
  childShouldHaveBirthCertificate,
  childShouldHavePassport,
  getActiveChildPassport,
  (
    { birthPlace: { value } },
    haveBC,
    shouldHaveDocument,
    { type: dulType }
  ) => {
    return shouldHaveDocument
      ? !!dulType && notEmptyField(value)
      : haveBC && notEmptyField(value);
  }
);

// child russian passport

export const validateChildDocumentDivisionCode = createSelector(
  getActiveChildPassport,
  childShouldHavePassport,
  childShouldHaveRussianPassport,
  ({ divisionCode, type }, hasDocument, isRussianPassport) =>
    hasDocument &&
    isRussianPassport &&
    validateDocumentDivisionCode(isRussianPassport, divisionCode.value)
);

// child address

export const validateChildAddressCheckbox = createSelector(
  child,
  isChild18YearsOld,
  isSpouseRegisteredInMoscow,
  ({ addressMatchWithApplicant }, is18YearsOld) =>
    !is18YearsOld && notEmptyField(addressMatchWithApplicant.value)
);

export const validateChildHomestay = createSelector(
  child,
  isChild18YearsOld,
  childAddressMatchesNoOne,
  isPaternityOrDivorced,
  (
    { confirmingDocs },
    is18YearsOld,
    addressMatchesNoOne,
    isPaternityOrDivorced
  ) =>
    !is18YearsOld &&
    addressMatchesNoOne &&
    isPaternityOrDivorced &&
    !confirmingDocs.value
);

// child family info

export const validateChildBirthStatus = createSelector(
  child,
  isApplicantMarried,
  ({ bornInCurrentMarriage }, applicantIsMarried) =>
    applicantIsMarried && !bornInCurrentMarriage.value
);

export const validateBornNotInCurrentMarriage = createSelector(
  child,
  isApplicantMarried,
  ({ bornInCurrentMarriage, bornInNotCurrentMarriage }, applicantIsMarried) => {
    return (
      applicantIsMarried &&
      bornInCurrentMarriage.value === BORN_IN.notCurrentMarriage &&
      !bornInNotCurrentMarriage.value
    );
  }
);

export const validateChildSecondParent: (state: {
  child: ChildFields;
  applicant: ApplicantFields;
}) => boolean = createSelector(
  child,
  getActiveChildSecondParent,
  isApplicantMarried,
  ({ bornInCurrentMarriage }, { status }, applicantIsMarried) => {
    if (
      applicantIsMarried &&
      bornInCurrentMarriage.value === BORN_IN.currentMarriage
    )
      return false;
    return !status.value;
  }
);

export const validatePaternityRegisterDate = createSelector(
  getActiveChildSecondParent,
  isPaternity,
  validateChildBirthdateWithPaternityDate,
  (
    { paternityRegisterDate: { value } }: PaternitySecondParent,
    isPaternity,
    error
  ) => isPaternity && (isDateInAcceptableRange(value) || error)
);

export const validatePaternityRegisterNumber = createSelector(
  getActiveChildSecondParent,
  isPaternity,
  ({ paternityRegisterNumber }: PaternitySecondParent, isPaternity) =>
    isPaternity && !paternityRegisterNumber.value
);

export const validateWidowhood = createSelector(
  getActiveChildSecondParent,
  isWidowed,
  ({ proofOfDeath }: DeadSecondParent, isWidowed) =>
    isWidowed && !proofOfDeath.value
);

export const validateSecondParentName = createSelector(
  getActiveChildSecondParent,
  secondParentStatus,
  ({ name }, status) => status && isNameValid(name.value)
);

export const validateSecondParentPatronymic = createSelector(
  getActiveChildSecondParent,
  secondParentStatus,
  ({ patronymic }, status) => status && isNameValid(patronymic.value)
);

export const validateSecondParentSurname = createSelector(
  getActiveChildSecondParent,
  secondParentStatus,
  ({ surname }, status) => status && isNameValid(surname.value)
);

export const validateSecondParentBirthdate = createSelector(
  getActiveChildSecondParent,
  hasDeathCertificate,
  validateSecondParentBirthdateAndDeathdate,
  ({ birthdate: { value } }: DeadSecondParent, deathCertificate, error) => {
    return deathCertificate && (isDateInAcceptableRange(value) || error);
  }
);

export const validateSecondParentDeathdate = createSelector(
  getActiveChildSecondParent,
  hasDeathCertificate,
  validateSecondParentBirthdateAndDeathdate,
  ({ deathdate: { value } }: DeadSecondParent, deathCertificate, error) => {
    return deathCertificate && (isDateInAcceptableRange(value) || error);
  }
);

export const validateSecondParentDeathCertIssuer = createSelector(
  getActiveChildSecondParent,
  hasDeathCertificate,
  ({ deathDocIssuer }: DeadSecondParent, deathCertificate) =>
    deathCertificate && notEmptyField(deathDocIssuer.value)
);

export const validateSecondParentMarriageDate = createSelector(
  getActiveChildSecondParent,
  isSecondParentDivorced,
  validateMarriageDateAndApplicantBirthDate,
  validateMarriageAndDivorceDates,
  (
    { dateOfMarriage: { value } }: DivorcedSecondParent,
    isDivorced,
    isEarlierApplicantBirth,
    error
  ) => {
    return (
      isDivorced &&
      (isDateInAcceptableRange(value) || isEarlierApplicantBirth || error)
    );
  }
);

export const validateSecondParentDivorceDate = createSelector(
  getActiveChildSecondParent,
  isSecondParentDivorced,
  validateMarriageAndDivorceDates,
  ({ dateOfDivorce: { value } }: DivorcedSecondParent, isDivorced, error) => {
    return isDivorced && (isDateInAcceptableRange(value) || error);
  }
);

export const validateSecondParentDivorceCertIssuer = createSelector(
  getActiveChildSecondParent,
  isSecondParentDivorced,
  ({ divorceDocIssuer }: DivorcedSecondParent, isDivorced) =>
    isDivorced && notEmptyField(divorceDocIssuer.value)
);

export const validateChildAddressOMK = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) => shouldValidate && validateAddressOMK(address)
);

export const validateChildAddressArea = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) =>
    shouldValidate && validateAddressArea({ address })
);

export const validateChildAddressFlat = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) =>
    shouldValidate && validateAddressFlat({ address })
);

export const validateChildAddressDistrict = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) =>
    shouldValidate && validateAddressDistrict({ address })
);

export const validateChildAddressStreet = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) =>
    shouldValidate && validateAddressStreet({ address })
);

export const validateChildAddressHouseAndBuilding = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) =>
    shouldValidate && validateAddressHouseAndBuilding({ address })
);

export const validateChildAddressAddress = createSelector(
  shouldValidateChildAddress,
  getChildAddress,
  (shouldValidate, address) =>
    shouldValidate && validateAddressAddress({ address })
);

export const validateChildClinicHelper = createSelector(
  child,
  showHomestayDocumentsSelector,
  ({ polyclinic }, showHomestay) => {
    const polyclinicName = polyclinic.institutionName.value;
    const polyclinicNotFound = !polyclinic.isFoundInCatalog;
    const selectFromCatalog = polyclinic.suggestions.some(
      (item) => item.text === polyclinicName
    );
    return (
      showHomestay &&
      !(
        selectFromCatalog ||
        (polyclinicNotFound && !notEmptyField(polyclinicName))
      )
    );
  }
);

export const validateChildEducationHelper = createSelector(
  child,
  showEducationSelector,
  (childData, showEducation) => {
    const { education, isStudying } = childData;
    const notStudying = isStudying !== null && !isStudying;
    const educationName = education.institutionName.value;
    const educationNotFound = !education.isFoundInCatalog;
    const selectFromCatalog = education.suggestions.some(
      (item) => item.text === educationName
    );

    return (
      showEducation &&
      !notStudying &&
      !(
        selectFromCatalog ||
        (educationNotFound && !notEmptyField(educationName))
      )
    );
  }
);

export const validateChildClinic = createSelector(
  shouldBeGettingEducationSelector,
  validateChildClinicHelper,
  validateChildEducationHelper,
  (shouldBeGettingEducation, validateClinic, validateEducation) => {
    if (shouldBeGettingEducation) return false;

    return ![validateClinic, validateEducation].some((val) => !val);
  }
);

export const validateChildEducation = createSelector(
  shouldBeGettingEducationSelector,
  validateChildClinicHelper,
  validateChildEducationHelper,
  (shouldBeGettingEducation, validateClinic, validateEducation) => {
    if (shouldBeGettingEducation) {
      return validateEducation;
    }

    return ![validateClinic, validateEducation].some((val) => !val);
  }
);

export const validateChildBirthDate = createSelector(
  child,
  validateBirthCertificateIssueDate,
  validateChildDocumentIssuedDate,
  validatePaternityRegisterDate,
  getActiveChildSecondParent,
  (
    {
      birthdate: { value },
      dul: {
        issueDate: { value: dulIssueDate },
      },
    },
    certificateIssueDateError,
    documentIssuedDateError,
    paternityRegisterDateError,
    { paternityRegisterDate }: PaternitySecondParent
  ) => {
    return (
      isDateInAcceptableRange(value) ||
      certificateIssueDateError ||
      (dulIssueDate && documentIssuedDateError) ||
      (!!paternityRegisterDate.value && paternityRegisterDateError)
    );
  }
);
